import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core';
import { Edit as EditIcon, Eye as EyeIcon, Plus as PlusIcon, Trash2 as TrashIcon, UserPlus as UserPlusIcon } from 'react-feather';
import { api } from 'src/service';

const DiscountCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openRedemptions, setOpenRedemptions] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [form, setForm] = useState({
    code: '',
    discount_percent: '',
    start_date: '',
    end_date: '',
    max_redemptions: '',
  });
  const [error, setError] = useState('');

  // Assign-to-customer dialog state.
  const [openAssign, setOpenAssign] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/discount-codes');
      setCodes(res.data.data);
    } catch (err) {
      console.error('Error fetching discount codes:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const handleCreate = async () => {
    setError('');
    try {
      await api.post('/backoffice/discount-codes', {
        code: form.code,
        discount_percent: parseInt(form.discount_percent),
        start_date: form.start_date,
        end_date: form.end_date,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
      });
      setOpenCreate(false);
      setForm({ code: '', discount_percent: '', start_date: '', end_date: '', max_redemptions: '' });
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.moreInformation?.error || 'Error creating code');
    }
  };

  const handleUpdate = async () => {
    setError('');
    try {
      const updates = {};
      if (form.discount_percent) updates.discount_percent = parseInt(form.discount_percent);
      if (form.start_date) updates.start_date = form.start_date;
      if (form.end_date) updates.end_date = form.end_date;
      updates.max_redemptions = form.max_redemptions ? parseInt(form.max_redemptions, 10) : null;
      if (form.status !== undefined) updates.status = form.status;

      await api.put(`/backoffice/discount-codes/${selectedCode.id}`, updates);
      setOpenEdit(false);
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.moreInformation?.error || 'Error updating code');
    }
  };

  const handleToggleStatus = async (code) => {
    try {
      const newStatus = code.status === 'active' ? 'inactive' : 'active';
      await api.put(`/backoffice/discount-codes/${code.id}`, { status: newStatus });
      fetchCodes();
    } catch (err) {
      alert(err.response?.data?.moreInformation?.error || 'Error toggling status');
    }
  };

  const handleViewRedemptions = async (code) => {
    setSelectedCode(code);
    try {
      const res = await api.get(`/backoffice/discount-codes/${code.id}/redemptions`);
      setRedemptions(res.data.data);
      setOpenRedemptions(true);
    } catch (err) {
      console.error('Error fetching redemptions:', err);
    }
  };

  const openAssignDialog = (code) => {
    setSelectedCode(code);
    setClientSearch('');
    setClientResults([]);
    setError('');
    setOpenAssign(true);
  };

  const searchClients = async () => {
    if (!clientSearch.trim()) {
      setClientResults([]);
      return;
    }
    setSearchingClients(true);
    try {
      const res = await api.post('/backoffice/clients/filter', {
        search: clientSearch.trim(),
        page_size: 10,
        current_page: 1,
      });
      // clientsSearch returns paginated chunks; take the first page.
      const payload = res.data && res.data.data;
      const list = Array.isArray(payload) ? payload : (payload && payload.data) || [];
      setClientResults(list);
    } catch (err) {
      console.error('Error searching clients:', err);
      setClientResults([]);
    }
    setSearchingClients(false);
  };

  const handleAssign = async (client) => {
    if (!selectedCode) return;
    setAssigning(true);
    try {
      const res = await api.post(`/backoffice/discount-codes/${selectedCode.id}/assign`, {
        client_id: client.id,
      });
      const warnings = res.data?.data?.warnings || [];
      const name = `${client.firstname || ''} ${client.lastname || ''}`.trim();
      if (warnings.length) {
        // eslint-disable-next-line no-alert
        window.alert(`Assigned to ${name}.\n\nNote:\n- ${warnings.join('\n- ')}`);
      } else {
        // eslint-disable-next-line no-alert
        window.alert(`Code ${selectedCode.code} assigned to ${name}.`);
      }
      setOpenAssign(false);
      fetchCodes();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(err.response?.data?.moreInformation?.error || 'Error assigning code');
    }
    setAssigning(false);
  };

  const handleDelete = async (code) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove discount code "${code.code}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/backoffice/discount-codes/${code.id}`);
      fetchCodes();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(err.response?.data?.moreInformation?.error || 'Error removing code');
    }
  };

  const openEditDialog = (code) => {
    setSelectedCode(code);
    setForm({
      code: code.code,
      discount_percent: String(code.discount_percent),
      start_date: code.start_date ? code.start_date.substring(0, 16) : '',
      end_date: code.end_date ? code.end_date.substring(0, 16) : '',
      max_redemptions: code.max_redemptions ? String(code.max_redemptions) : '',
      status: code.status,
    });
    setError('');
    setOpenEdit(true);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-PT', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatLimit = (code) => {
    if (!code.max_redemptions) return 'Unlimited';
    return `${code.active_redemptions_count || 0} / ${code.max_redemptions}`;
  };

  return (
    <>
      <Helmet>
        <title>Washer - Discount Codes</title>
      </Helmet>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4">Discount Codes</Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setForm({ code: '', discount_percent: '', start_date: '', end_date: '', max_redemptions: '' });
                setError('');
                setOpenCreate(true);
              }}
              startIcon={<PlusIcon />}
            >
              New Code
            </Button>
          </Box>

          <Card>
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Discount %</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Limit</TableCell>
                      <TableCell>Redemptions</TableCell>
                      <TableCell>Used</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.id}>
                        <TableCell><strong>{code.code}</strong></TableCell>
                        <TableCell>{code.discount_percent}%</TableCell>
                        <TableCell>{formatDate(code.start_date)}</TableCell>
                        <TableCell>{formatDate(code.end_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={code.status}
                            color={code.status === 'active' ? 'primary' : 'default'}
                            size="small"
                            onClick={() => handleToggleStatus(code)}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>{formatLimit(code)}</TableCell>
                        <TableCell>{code.redemptions_count}</TableCell>
                        <TableCell>{code.used_count}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => openAssignDialog(code)} title="Assign to customer">
                            <UserPlusIcon size={16} color="#2e7d32" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openEditDialog(code)} title="Edit">
                            <EditIcon size={16} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleViewRedemptions(code)} title="View Redemptions">
                            <EyeIcon size={16} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(code)} title="Remove">
                            <TrashIcon size={16} color="#d32f2f" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {codes.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No discount codes yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Create Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Discount Code</DialogTitle>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                fullWidth
                placeholder="e.g. LAUNCH2026"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Discount %"
                type="number"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                fullWidth
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="datetime-local"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="datetime-local"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Redemption Limit"
                type="number"
                value={form.max_redemptions}
                onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                fullWidth
                helperText="Leave blank for unlimited"
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} color="primary" variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Discount Code: {selectedCode?.code}</DialogTitle>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Discount %"
                type="number"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                fullWidth
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status === 'active'}
                    onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'inactive' })}
                  />
                }
                label={form.status === 'active' ? 'Active' : 'Inactive'}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="datetime-local"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="datetime-local"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Redemption Limit"
                type="number"
                value={form.max_redemptions}
                onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                fullWidth
                helperText="Leave blank for unlimited"
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleUpdate} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Redemptions Dialog */}
      <Dialog open={openRedemptions} onClose={() => setOpenRedemptions(false)} maxWidth="md" fullWidth>
        <DialogTitle>Redemptions: {selectedCode?.code}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Redeemed At</TableCell>
                  <TableCell>Used At</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptions.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.firstname} {r.lastname}</TableCell>
                    <TableCell>{r.phonenumber}</TableCell>
                    <TableCell>{formatDate(r.redeemed_at)}</TableCell>
                    <TableCell>{r.used_at ? formatDate(r.used_at) : '-'}</TableCell>
                    <TableCell>
                      {r.order_id ? (
                        <a href={`/app/jobs/edit/${r.order_id}`}>{String(r.order_id).substring(0, 8)}...</a>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.expired ? 'Expired' : r.used_at ? 'Used' : 'Pending'}
                        color={r.used_at ? 'primary' : r.expired ? 'default' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {redemptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No redemptions yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRedemptions(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign-to-customer Dialog */}
      <Dialog open={openAssign} onClose={() => setOpenAssign(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign {selectedCode?.code} to a customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            The customer will see this discount already active in their app — no need to type the code.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Search by name, email or phone"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') searchClients(); }}
              fullWidth
              autoFocus
            />
            <Button onClick={searchClients} variant="contained" color="primary" disabled={searchingClients}>
              {searchingClients ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>

          <List dense>
            {clientResults.map((c) => (
              <ListItem
                key={c.id}
                button
                divider
                disabled={assigning}
                onClick={() => handleAssign(c)}
              >
                <ListItemText
                  primary={`${c.firstname || ''} ${c.lastname || ''}`.trim() || '(no name)'}
                  secondary={`${c.email || ''} ${c.phonenumber ? '· ' + c.phonenumber : ''}`}
                />
                <Button size="small" variant="outlined" color="primary" disabled={assigning}>
                  Assign
                </Button>
              </ListItem>
            ))}
            {clientResults.length === 0 && !searchingClients && clientSearch.trim() !== '' && (
              <ListItem>
                <ListItemText secondary="No customers found. Try a different search." />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssign(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DiscountCodes;
